import AssessmentHistoryComparisonCard, {
  type AssessmentHistoryComparisonEntry,
} from '../components/AssessmentHistoryComparisonCard';
import {
  formatPeakFlowPercent,
  formatPeakFlowValue,
  getPeakFlowScoreColor,
  summarizePeakFlowPayload,
  type PeakFlowResultPayload,
} from '../peak-flow/peakFlowDomain';

interface PeakFlowResultRendererProps {
  payload: PeakFlowResultPayload | null | undefined;
  assessmentDate?: string | null;
  comparisonHistory?: AssessmentHistoryComparisonEntry[];
}

const SummaryMetric = ({ label, value, note }: { label: string; value: string; note?: string | null }) => (
  <div className="rounded-[24px] border border-slate-100 bg-white p-5 shadow-sm">
    <div className="text-[10px] font-black uppercase tracking-[0.22em] text-slate-400">{label}</div>
    <div className="mt-2 text-3xl font-black text-slate-900">{value}</div>
    {note ? <div className="mt-2 text-sm text-slate-500">{note}</div> : null}
  </div>
);

const FlagList = ({ title, items, danger = false }: { title: string; items: string[]; danger?: boolean }) => {
  if (!items.length) return null;

  return (
    <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
      <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">{title}</div>
      <div className="mt-4 flex flex-wrap gap-3">
        {items.map((item) => (
          <span
            key={item}
            className={`rounded-full border px-4 py-2 text-sm font-bold ${
              danger ? 'border-rose-200 bg-rose-50 text-rose-900' : 'border-primary-200 bg-primary-50 text-primary-800'
            }`}
          >
            {item}
          </span>
        ))}
      </div>
    </div>
  );
};

const MeasurementCard = ({
  title,
  readings,
  highlightValue,
}: {
  title: string;
  readings: number[];
  highlightValue: number | null;
}) => {
  if (!readings.length) return null;

  return (
    <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
      <div className="flex items-center justify-between gap-3">
        <div>
          <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">{title}</div>
          <div className="mt-2 text-3xl font-black text-slate-900">
            {highlightValue != null ? `${formatPeakFlowValue(highlightValue)} L/min` : '—'}
          </div>
        </div>
        <div className="rounded-full bg-slate-100 px-3 py-1 text-xs font-bold text-slate-700">Melhor de {readings.length}</div>
      </div>

      <div className="mt-5 grid gap-3 md:grid-cols-3">
        {readings.map((reading, index) => {
          const active = highlightValue != null && reading === highlightValue;
          return (
            <div
              key={`${title}-${index}`}
              className={`rounded-[20px] border p-4 ${
                active ? 'border-primary-300 bg-primary-50/70' : 'border-slate-200 bg-slate-50/70'
              }`}
            >
              <div className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">{index + 1}ª manobra</div>
              <div className="mt-2 text-xl font-black text-slate-900">{formatPeakFlowValue(reading)}</div>
              <div className="text-sm text-slate-500">L/min</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default function PeakFlowResultRenderer({
  payload,
  assessmentDate,
  comparisonHistory = [],
}: PeakFlowResultRendererProps) {
  const summary = summarizePeakFlowPayload(payload);
  const scoreColor = getPeakFlowScoreColor(payload);
  const hasReference = summary.referenceMode !== 'none' && summary.referenceValue != null;
  const metricCards = [
    summary.referencePercentPre != null ? (
      <SummaryMetric
        key="reference"
        label="% da referência"
        value={formatPeakFlowPercent(summary.referencePercentPre)}
        note={`${summary.zoneLabel} • ${summary.referenceLabel}`}
      />
    ) : null,
    summary.bestPostFlow != null ? (
      <SummaryMetric
        key="post"
        label="Melhor pós-broncodilatador"
        value={`${formatPeakFlowValue(summary.bestPostFlow)} L/min`}
        note={`${summary.postAttemptCount} manobras válidas`}
      />
    ) : null,
    summary.bronchodilatorDeltaPercent != null ? (
      <SummaryMetric
        key="response"
        label="Variação após broncodilatador"
        value={formatPeakFlowPercent(summary.bronchodilatorDeltaPercent)}
        note={
          summary.bronchodilatorDeltaAbsolute != null
            ? `${formatPeakFlowValue(summary.bronchodilatorDeltaAbsolute)} L/min`
            : null
        }
      />
    ) : null,
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      <div className="grid gap-6 md:grid-cols-5">
        <div className="rounded-[28px] border border-slate-200 bg-white p-8 text-center shadow-sm md:col-span-2">
          <p className="mb-4 text-xs font-black uppercase tracking-[0.25em] text-primary-700">Pico de fluxo expiratório</p>
          <div className="text-6xl font-black leading-none" style={{ color: scoreColor }}>
            {summary.bestPreFlow != null ? formatPeakFlowValue(summary.bestPreFlow) : '—'}
          </div>
          <div className="mt-2 text-sm font-semibold text-slate-700">L/min</div>
          <div className="mt-5 inline-flex items-center gap-2 text-sm font-black" style={{ color: scoreColor }}>
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: scoreColor }} />
            {summary.severity}
          </div>
          <div className="mt-4 text-sm font-semibold text-slate-500">
            Melhor de {summary.preAttemptCount} manobras
            {summary.referencePercentPre != null
              ? ` • ${summary.zoneLabel} • ${formatPeakFlowPercent(summary.referencePercentPre)} da referência`
              : ' • sem referência para classificação em zonas'}
          </div>
        </div>

        <div className="flex flex-col justify-center md:col-span-3">
          <h4 className="text-2xl font-black tracking-tight text-slate-800">Interpretação</h4>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-slate-600">{summary.interpretation}</p>

          {hasReference ? (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
              <span className="font-semibold text-slate-900">Referência:</span>{' '}
              {summary.referenceLabel} ({formatPeakFlowValue(summary.referenceValue)} L/min)
              {summary.referenceMode === 'predicted_reference' && summary.referenceSourceLabel ? (
                <div className="mt-2 text-xs leading-relaxed text-slate-500">
                  Fonte: {summary.referenceSourceLabel}
                  {summary.referencePopulationNote ? ` • ${summary.referencePopulationNote}` : ''}
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>

      {metricCards.length ? <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-3">{metricCards}</div> : null}

      <div className={`grid gap-6 ${summary.bestPostFlow != null ? 'xl:grid-cols-2' : ''}`}>
        <MeasurementCard
          title="Manobras pré-broncodilatador"
          readings={payload?.preReadings ?? []}
          highlightValue={summary.bestPreFlow}
        />
        {summary.bronchodilatorUsed ? (
          <MeasurementCard
            title="Manobras pós-broncodilatador"
            readings={payload?.postReadings ?? []}
            highlightValue={summary.bestPostFlow}
          />
        ) : null}
      </div>

      {summary.symptomLabels.length || summary.dangerSignLabels.length ? (
        <div className="grid gap-6 xl:grid-cols-2">
          <FlagList title="Sintomas marcados" items={summary.symptomLabels} />
          <FlagList title="Sinais de alarme" items={summary.dangerSignLabels} danger />
        </div>
      ) : null}

      <div className="rounded-[24px] border border-slate-100 bg-white p-6 shadow-sm">
        <div className="text-[10px] font-black uppercase tracking-[0.24em] text-slate-400">Base de interpretação</div>
        <p className="mt-4 text-sm leading-relaxed text-slate-600">{summary.referenceNote}</p>
      </div>

      <AssessmentHistoryComparisonCard
        testKey="PEAK_FLOW"
        currentPayload={payload}
        currentAssessmentDate={assessmentDate}
        history={comparisonHistory}
      />
    </div>
  );
}
